import { useAppDispatch, useAppSelector } from 'store/hooks';
import { closeModal, selectActiveModalData } from 'features/modal/modalSlice';
import { Trans, useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from 'components/Dialog';
import Button from '@mui/material/Button';
import { UserId } from 'features/users/types';
import { selectActiveUserById } from 'features/users/usersSlice';
import { PermissionTypes } from 'features/permissions/types';
import React, { useEffect, useId, useMemo, useState } from 'react';
import { usePermissions } from 'features/permissions/usePermissions';
import { Alert, Box, MenuItem, SelectChangeEvent } from '@mui/material';
import Select from 'components/Select';
import { banUser } from 'features/users/actions';

const BanUserConfirmation = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const [banDuration, setBanDuration] = useState('5');

  const prefix = useId();

  const modalData = useAppSelector(selectActiveModalData);
  const userId = modalData.userId as UserId;

  const user = useAppSelector((state) => selectActiveUserById(state, userId));

  const { hasPermissions } = usePermissions();

  const canBanParticipant = hasPermissions(PermissionTypes.banParticipants, {
    targetRole: user?.role,
  });

  const modalShouldBeClosed = !canBanParticipant || !user;

  useEffect(() => {
    if (modalShouldBeClosed) {
      dispatch(closeModal('banUserConfirmation'));
    }
  }, [modalShouldBeClosed, dispatch]);

  const handleOk = () => {
    const isPermanent = banDuration === 'permanent';

    dispatch(
      banUser({
        id: userId,
        permanent: isPermanent,
        duration: isPermanent ? undefined : Number(banDuration),
      })
    );
    dispatch(closeModal('banUserConfirmation'));
  };

  const handleCancel = () => {
    dispatch(closeModal('banUserConfirmation'));
  };

  const selectOptions = useMemo(
    () => [
      {
        value: '5',
        label: t('ban_user_confirm.ban_duration.5min'),
      },
      {
        value: '15',
        label: t('ban_user_confirm.ban_duration.15min'),
      },
      {
        value: '30',
        label: t('ban_user_confirm.ban_duration.30min'),
      },
      {
        value: '60',
        label: t('ban_user_confirm.ban_duration.60min'),
      },
      {
        value: 'permanent',
        label: t('ban_user_confirm.ban_duration.permanent'),
      },
    ],
    [t]
  );

  if (!user) {
    return null;
  }

  const handleInputChange = (event: SelectChangeEvent) => {
    setBanDuration(event.target.value);
  };

  return (
    <Dialog
      open={!modalShouldBeClosed}
      maxWidth="xs"
      aria-labelledby={`${prefix}-title`}
      aria-describedby={`${prefix}-description`}
    >
      <DialogTitle id={`${prefix}-title`}>
        {t('ban_user_confirm.title', { username: user.name })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          id={`${prefix}-description`}
          sx={{
            mb: 6,
          }}
        >
          <Trans
            i18nKey="modals:ban_user_confirm.description"
            t={t}
            values={{
              username: user.name,
            }}
            components={{
              bold: <strong />,
            }}
          />
        </DialogContentText>

        <Box
          sx={{
            mb: 6,
            maxWidth: 200,
          }}
        >
          <Select
            onChange={handleInputChange}
            value={banDuration}
            sx={{
              width: '100%',
            }}
          >
            {selectOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Alert severity="warning">{t('ban_user_confirm.warning_text')}</Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="grayscale">
          {t('ban_user_confirm.btn_cancel_label')}
        </Button>
        <Button onClick={handleOk}>{t('ban_user_confirm.btn_ok_label')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BanUserConfirmation;
